import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import TextLink from 'LuxKit/TextLink'
import { SET_OPTIMIZELY_FEATURE_FLAGS } from 'actions/actionConstants'
import BodyText from 'LuxKit/Typography/BodyText'
import SwitchButton from 'LuxKit/Switch/SwitchButton'
import Group from 'components/utils/Group'
import { formatOptimizelyTitle } from './ExperimentPanelHelpers'

interface FeatureFlagItemProps {
  flagKey: string
  enabled: boolean
}

function FeatureFlagItem({ flagKey, enabled }: FeatureFlagItemProps) {
  const intl = useIntl()
  const dispatch = useAppDispatch()

  const handleToggle = useCallback(() => {
    dispatch({
      type: SET_OPTIMIZELY_FEATURE_FLAGS,
      flags: {
        [flagKey]: !enabled,
      },
    })
  }, [dispatch, flagKey, enabled])

  const title = useMemo(() => formatOptimizelyTitle(flagKey), [flagKey])
  const featureFlagTypeLabel = intl.formatMessage({
    id: 'temp_feature_flag_item_type_label',
    defaultMessage: 'Feature Flag',
    description: 'Type label shown for a feature flag entry in the internal Optimizely control panel',
  })
  const enabledLabel = intl.formatMessage({
    id: 'temp_feature_flag_item_enabled_label',
    defaultMessage: 'On',
    description: 'Toggle label shown when a feature flag is enabled in the internal Optimizely control panel',
  })
  const disabledLabel = intl.formatMessage({
    id: 'temp_feature_flag_item_disabled_label',
    defaultMessage: 'Off',
    description: 'Toggle label shown when a feature flag is disabled in the internal Optimizely control panel',
  })

  return (
    <Group direction="vertical" gap={20}>
      <Group direction="vertical" gap={4}>
        <TextLink
          underline={false}
          href={`https://app.optimizely.com/v2/projects/24799170005/flags/manage/${flagKey}`}
          target="_blank"
          weight="regular"
        >
          {title}
        </TextLink>
        <BodyText variant="small" colour="light-foreground-alt">
          {flagKey}
        </BodyText>
      </Group>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="center">
        <BodyText variant="medium" colour="light-foreground-alt">
          <i>{featureFlagTypeLabel}</i>
        </BodyText>
        <SwitchButton checked={enabled} onChange={handleToggle}>
          {enabled ? enabledLabel : disabledLabel}
        </SwitchButton>
      </Group>
    </Group>
  )
}

export default FeatureFlagItem
